import type { H3Event } from "h3";
import type { ProductListItem, ProductOverpackItem, ProductPackagingInfo } from "~~/models/products";
import type { PlanktonProductQuery, PlanktonProductRelationQuery } from "~~/server/plankton";
import { z } from "zod";
import { PlanktonProductRelationType } from "~~/server/plankton";

export const productQuerySchema = z.object({
  productIdList: z.union([z.coerce.number().array(), z.coerce.number()])
    .transform(value => typeof value === "number" ? [value] : value),
  addCategories: z.coerce.boolean().optional(),
  addExternalData: z.coerce.boolean().optional(),
  addFiles: z.coerce.boolean().optional(),
  addOverpacks: z.coerce.boolean().optional(),
  addOverpackTranslations: z.coerce.boolean().optional(),
  AddProductRelations: z.custom<Array<PlanktonProductRelationType>>().optional(),
  AddProductRelationsOnlyOnlineProducts: z.coerce.boolean().optional(),
  addProductTranslations: z.coerce.boolean().optional(),
  addPropertyValues: z.coerce.boolean().optional(),
  addStoreData: z.coerce.boolean().optional(),
});

export type ProductQuery = z.infer<typeof productQuerySchema>;

export async function createPlanktonProductQuery(event: H3Event) {
  const request = await getValidatedQuery(event, productQuerySchema.parse);
  const store = event.context.store;

  return {
    ProductIdList: request.productIdList,
    LcidList: [event.context.lcid || 2067],
    AddCategories: request.addCategories,
    AddExternalData: request.addExternalData,
    AddFiles: request.addFiles,
    AddOverpacks: request.addOverpacks,
    AddOverpackTranslations: request.addOverpackTranslations,
    AddProductRelations: request.AddProductRelations,
    AddProductRelationsOnlyOnlineProducts: request.AddProductRelationsOnlyOnlineProducts,
    AddProductTranslations: request.addProductTranslations,
    AddPropertyValues: request.addPropertyValues,
    AddStoreData: request.addStoreData,
    AnalyticsLcid: store.analyticsLcid,
    AnalyticsBrandSource: store.analyticsBrandSource,
    AnalyticsBrandSourceId: store.analyticsBrandSourceId,
    AnalyticsProductGroupRootId: store.navigationRootCategoryId,
  } satisfies PlanktonProductQuery;
}

export const productRelationQuerySchema = z.object({
  productIdList: z.union([z.coerce.number().array(), z.coerce.number()])
    .transform(value => typeof value === "number" ? [value] : value),
  productRelationTypeIdList: z.union([z.coerce.number().array(), z.coerce.number()])
    .transform(value => typeof value === "number" ? [value] : value),
});

export type ProductRelationQuery = z.infer<typeof productRelationQuerySchema>;

export async function createPlanktonProductRelationQuery(event: H3Event): Promise<PlanktonProductRelationQuery> {
  const request = await getValidatedQuery(event, productRelationQuerySchema.parse);

  return {
    ProductIdList: request.productIdList,
    Relations: request.productRelationTypeIdList,
  } as PlanktonProductRelationQuery;
}

export const productReplacementsQuerySchema = z.object({
  productIdList: z.union([z.coerce.number().array(), z.coerce.number()])
    .transform(value => typeof value === "number" ? [value] : value),
});

export type ProductReplacementsQuery = z.infer<typeof productReplacementsQuerySchema>;

export async function createPlanktonProductReplacementsQuery(event: H3Event): Promise<PlanktonProductRelationQuery> {
  const request = await getValidatedQuery(event, productReplacementsQuerySchema.parse);
  const productRelationTypeIdList = [PlanktonProductRelationType.HasAlternative];
  return {
    ProductIdList: request.productIdList,
    Relations: productRelationTypeIdList,
  } as PlanktonProductRelationQuery;
}

export function determinePackaging(selectedOverpackId: number, product?: ProductListItem | null): ProductPackagingInfo | null {
  if (!product) {
    return null;
  }

  const overpacks = product?.overpacks;

  const selectedOverpack = overpacks?.find((o: ProductOverpackItem) => o.value === selectedOverpackId)
    ?? product?.defaultOverpack
    ?? overpacks?.at(0)
    ?? null;

  if (!selectedOverpack || selectedOverpack.unitsPerPack === 1) {
    return null;
  }

  const singleOverpack = overpacks?.find((o: ProductOverpackItem) => o.unitsPerPack === 1);

  if (!singleOverpack) {
    // `${selectedOverpack.unitsPerPack} Stuks in 1 ${selectedOverpack.singleName}`
    return {
      units: `${selectedOverpack.unitsPerPack}`,
      container: `1 ${selectedOverpack.singleName}`,
      type: "default",
    };
  }

  // `${selectedOverpack.unitsPerPack} ${singleOverpack.pluralName} in ${singleOverpack.unitsPerPack} ${selectedOverpack.singleName}`;
  return {
    units: `${selectedOverpack.unitsPerPack} ${singleOverpack.pluralName}`,
    container: `${singleOverpack.unitsPerPack} ${selectedOverpack.singleName}`,
    type: "normal",
  };
}

export function determineOverpackCode(selectedOverpackId: number, product?: ProductListItem | null | undefined): string {
  if (!product) {
    return "";
  }

  const overpacks = product?.overpacks;

  const selectedOverpack = overpacks?.find((o: ProductOverpackItem) => o.value === selectedOverpackId)
    ?? product?.defaultOverpack
    ?? overpacks?.at(0)
    ?? null;

  return selectedOverpack?.code ?? "";
}

export function determineOverpackValue(selectedOverpackId: number, product?: ProductListItem | null | undefined): number {
  if (!product) {
    return 0;
  }

  const overpacks = product?.overpacks;

  const selectedOverpack = overpacks?.find((o: ProductOverpackItem) => o.value === selectedOverpackId)
    ?? product?.defaultOverpack
    ?? overpacks?.at(0)
    ?? null;

  return selectedOverpack?.value ?? 0;
}
